/**
 * Calendars Context
 * Provides centralized calendar data fetching and caching for the app
 * Data is structured by integration (Google account) with nested calendars
 */

import React, { createContext, useContext, useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from './auth-context'
import { openOAuthPopup } from '@/lib/oauth-popup'
import type { CalendarResource as BaseCalendarResource } from '@/components/automation-rules/types'

/**
 * Extended calendar resource with status from integrations_calendars table
 */
export interface CalendarResource extends BaseCalendarResource {
  /** Internal ID from integrations_calendars table (null if not yet added) */
  integrationCalendarId?: string
  /** Whether automation rules are active for this calendar */
  automationIsActive?: boolean
  /** Whether analytics tracking is active for this calendar */
  analyticsIsActive?: boolean
  /** When true, calendar is planning-only (analytics for planning, not actual results) */
  planningOnly?: boolean
  /** Whether AI color coding is active for this calendar */
  aiColorCodingIsActive?: boolean
}

/**
 * Provider information for OAuth reconnection
 */
export interface IntegrationProvider {
  id: string
  display_name: string
  logo: string
  url: string
  client_id_app_env_var: string
}

/**
 * Integration with its calendars and connection status
 * isDisconnected belongs at this level since all calendars share the same OAuth token
 */
export interface CalendarIntegration {
  id: string
  provider_email: string
  isDisconnected: boolean
  calendars: CalendarResource[]
  calendarCount: number
  provider: IntegrationProvider
}

interface CalendarsContextType {
  /** List of integrations with their nested calendars */
  integrations: CalendarIntegration[]
  /** Loading state for initial fetch */
  loading: boolean
  /** Re-fetch all integrations and calendars from database */
  refreshCalendars: () => Promise<void>
  /** Refresh calendars for a specific integration via Google API */
  refreshCalendarsForIntegration: (integrationId: string) => Promise<void>
  /** Reconnect a disconnected integration via OAuth */
  reconnect: (integrationId: string) => Promise<void>
  /** Disconnect/delete an integration */
  disconnect: (integrationId: string) => Promise<void>
  /** Find a calendar by integration ID and calendar ID */
  findCalendar: (integrationId: string, calendarId: string) => CalendarResource | undefined
}

const CalendarsContext = createContext<CalendarsContextType | undefined>(undefined)

// Helper function to check if refresh token is expired
function checkTokenExpired(expiresAt?: string): boolean {
  if (!expiresAt) return false
  try {
    const expirationDate = new Date(expiresAt)
    return expirationDate < new Date()
  } catch {
    return false
  }
}

// Helper function to normalize logo path (remove public/ prefix, fix backslashes)
function normalizeLogoPath(logoPath?: string): string {
  if (!logoPath) return ''
  return logoPath.replace(/\\/g, '/').replace(/^public\//, '/')
}

export function CalendarsProvider({ children }: { children: React.ReactNode }) {
  const { user, session } = useAuth()
  const [integrations, setIntegrations] = useState<CalendarIntegration[]>([])
  const [loading, setLoading] = useState(true)
  
  // Track active OAuth channels for proper cleanup
  const activeChannelsRef = useRef<Set<ReturnType<typeof supabase.channel>>>(new Set())

  // Cleanup channels on unmount
  useEffect(() => {
    return () => {
      activeChannelsRef.current.forEach(channel => {
        channel.unsubscribe()
      })
      activeChannelsRef.current.clear()
    }
  }, [])

  // Fetch all integrations with their calendars and provider data
  const fetchCalendars = useCallback(async () => {
    if (!user?.id) {
      setIntegrations([])
      setLoading(false)
      return
    }

    try {
      setLoading(true)

      // Fetch integrations with resources and provider data for calendar type
      const { data: integrationsData, error: integrationsError } = await supabase
        .from('integrations')
        .select(`
          id,
          provider_email,
          resources,
          refresh_token_expires_at,
          integrations_providers!inner(
            id,
            type,
            display_name,
            logo,
            url,
            client_id_app_env_var
          )
        `)
        .eq('integrations_providers.type', 'calendar')
        .eq('user_id', user.id)

      if (integrationsError) {
        console.error('Error fetching integrations:', integrationsError)
        return
      }

      // Fetch integrations_calendars for status data
      // Get integration IDs from the first query
      const integrationIds = integrationsData?.map(i => i.id) || []
      
      let calendarsStatusMap: Map<string, { id: string; automationIsActive: boolean; analyticsIsActive: boolean; planningOnly: boolean; aiColorCodingIsActive: boolean }> = new Map()
      
      if (integrationIds.length > 0) {
        const { data: calendarsStatusData, error: calendarsStatusError } = await supabase
          .from('integrations_calendars')
          .select('id, integration_id, calendar_id_external, automation_is_active, analytics_is_active, planning_only, ai_color_coding_is_active')
          .in('integration_id', integrationIds)

        if (calendarsStatusError) {
          console.error('Error fetching calendars status:', calendarsStatusError)
          // Continue without status data
        } else if (calendarsStatusData) {
          // Create a map for quick lookup: "integration_id:calendar_id_external" -> status
          calendarsStatusData.forEach((cs: any) => {
            const key = `${cs.integration_id}:${cs.calendar_id_external}`
            calendarsStatusMap.set(key, {
              id: cs.id,
              automationIsActive: cs.automation_is_active ?? false,
              analyticsIsActive: cs.analytics_is_active ?? false,
              planningOnly: cs.planning_only ?? false,
              aiColorCodingIsActive: cs.ai_color_coding_is_active ?? false,
            })
          })
        }
      }

      // Build nested structure: integrations with their calendars
      const allIntegrations: CalendarIntegration[] = []
      integrationsData?.forEach((integration: any) => {
        const calendars: CalendarResource[] = integration.resources?.calendars?.map((cal: BaseCalendarResource) => {
          // Look up status from integrations_calendars
          const statusKey = `${integration.id}:${cal.id}`
          const status = calendarsStatusMap.get(statusKey)
          
          return {
            id: cal.id,
            summary: cal.summary,
            primary: cal.primary,
            backgroundColor: cal.backgroundColor,
            foregroundColor: cal.foregroundColor,
            accessRole: cal.accessRole,
            // Add status fields from integrations_calendars
            integrationCalendarId: status?.id,
            automationIsActive: status?.automationIsActive,
            analyticsIsActive: status?.analyticsIsActive,
            planningOnly: status?.planningOnly,
            aiColorCodingIsActive: status?.aiColorCodingIsActive,
          }
        }) || []

        // Get provider data (handle both array and object response from Supabase)
        const providerData = Array.isArray(integration.integrations_providers)
          ? integration.integrations_providers[0]
          : integration.integrations_providers

        allIntegrations.push({
          id: integration.id,
          provider_email: integration.provider_email || 'Unknown Account',
          isDisconnected: checkTokenExpired(integration.refresh_token_expires_at),
          calendars,
          calendarCount: calendars.length,
          provider: {
            id: providerData?.id || '',
            display_name: providerData?.display_name || 'Unknown Provider',
            logo: normalizeLogoPath(providerData?.logo),
            url: providerData?.url || '',
            client_id_app_env_var: providerData?.client_id_app_env_var || '',
          },
        })
      })

      setIntegrations(allIntegrations)
    } catch (error) {
      console.error('Error fetching calendars:', error)
      setIntegrations([])
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  // Refresh calendars for a specific integration via Edge Function
  const refreshCalendarsForIntegration = useCallback(async (integrationId: string) => {
    if (!session?.access_token || !integrationId || !user?.id) return

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const edgeFunctionUrl = `${supabaseUrl}/functions/v1/integrations-calendars-list`
      
      const response = await fetch(edgeFunctionUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          integrationId: integrationId
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.messageUser || errorData.message || 'Failed to refresh resources')
      }

      const result = await response.json()
      
      if (!result?.success) {
        throw new Error(result.messageUser || result.message || 'Failed to refresh resources')
      }
      
      // Refetch all integrations to update the cache
      await fetchCalendars()
    } catch (error) {
      console.error('Error refreshing calendars:', error)
      throw error
    }
  }, [session?.access_token, user?.id, fetchCalendars])

  // Reconnect a disconnected integration via OAuth popup
  const reconnect = useCallback(async (integrationId: string) => {
    if (!session?.access_token || !user?.id) {
      throw new Error('Authentication required')
    }

    // Find the integration to get provider info
    const integration = integrations.find(i => i.id === integrationId)
    if (!integration) {
      throw new Error('Integration not found')
    }

    const { provider } = integration

    // Validate provider has required OAuth config
    const providerClientId = import.meta.env[provider.client_id_app_env_var]
    if (!providerClientId) {
      throw new Error(`${provider.display_name} is not configured`)
    }

    if (!provider.url) {
      throw new Error(`OAuth URL not configured for ${provider.display_name}`)
    }

    return new Promise<void>((resolve, reject) => {
      // Create a unique channel for this OAuth flow
      const channelId = `oauth_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      const channel = supabase.channel(channelId)

      const cleanupChannel = () => {
        channel.unsubscribe()
        activeChannelsRef.current.delete(channel)
      }

      activeChannelsRef.current.add(channel)

      let broadcastReceived = false

      // Listen for OAuth callback via broadcast
      channel
        .on('broadcast', { event: 'integration_complete' }, async (payload) => {
          broadcastReceived = true
          if (payload.payload.success) {
            // Add delay to ensure database transaction is committed
            await new Promise(r => setTimeout(r, 1000))
            await fetchCalendars()
            cleanupChannel()
            resolve()
          } else {
            cleanupChannel()
            if (payload.payload.error !== 'OAuth popup was closed') {
              reject(new Error(payload.payload.error || 'Reconnection failed'))
            } else {
              reject(new Error('OAuth popup was closed'))
            }
          }
        })
        .on('broadcast', { event: 'oauth_callback' }, async (payload) => {
          // Handle legacy event name for backward compatibility
          broadcastReceived = true
          if (payload.payload.success) {
            await new Promise(r => setTimeout(r, 1000))
            await fetchCalendars()
            cleanupChannel()
            resolve()
          } else {
            cleanupChannel()
            if (payload.payload.error !== 'OAuth popup was closed') {
              reject(new Error(payload.payload.error || 'Reconnection failed'))
            } else {
              reject(new Error('OAuth popup was closed'))
            }
          }
        })
        .subscribe()

      // Store OAuth state
      const oauthState = {
        integrationType: 'calendar' as const,
        returnView: 'list' as const
      }
      localStorage.setItem('integration_oauth_state', JSON.stringify(oauthState))

      // Construct OAuth URL
      const appOrigin = btoa(window.location.origin)
      const state = `${session.access_token}|||${appOrigin}|||${provider.id}|||${channelId}`
      const providerOAuthUrl = `${provider.url}&client_id=${providerClientId}&state=${encodeURIComponent(state)}`

      // Open OAuth popup
      openOAuthPopup({
        url: providerOAuthUrl,
        title: `Reconnect ${provider.display_name}`
      }).then(async (result) => {
        // Handle result from postMessage (fallback if broadcast doesn't work)
        if (result.success && !broadcastReceived) {
          await fetchCalendars()
          cleanupChannel()
          resolve()
        } else if (result.error && !broadcastReceived) {
          cleanupChannel()
          if (result.error !== 'OAuth popup was closed') {
            reject(new Error(result.error))
          } else {
            reject(new Error('OAuth popup was closed'))
          }
        }
      })

      // Cleanup after timeout (30 seconds)
      setTimeout(() => {
        if (activeChannelsRef.current.has(channel)) {
          cleanupChannel()
          if (!broadcastReceived) {
            reject(new Error('OAuth flow timed out'))
          }
        }
      }, 30000)
    })
  }, [session?.access_token, user?.id, integrations, fetchCalendars])

  // Disconnect/delete an integration
  const disconnect = useCallback(async (integrationId: string) => {
    if (!user?.id || !session?.access_token) {
      throw new Error('Authentication required')
    }

    // Find the integration for display purposes
    const integration = integrations.find(i => i.id === integrationId)
    if (!integration) {
      throw new Error('Integration not found')
    }

    try {
      // Call edge function to properly stop watch channels before deletion
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const response = await fetch(
        `${supabaseUrl}/functions/v1/integration-disconnect`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ integrationId }),
        }
      )

      const result = await response.json()

      if (!response.ok || !result.success) {
        console.error('Edge function error disconnecting integration:', result)
        throw new Error(result.error || 'Failed to disconnect integration')
      }

      // Refresh the integrations list
      await fetchCalendars()
      
      return // Success - let the caller handle the toast
    } catch (error) {
      console.error('Error disconnecting integration:', error)
      throw error
    }
  }, [user?.id, session?.access_token, integrations, fetchCalendars])

  // Helper to find a calendar by integration ID and calendar ID
  const findCalendar = useCallback((integrationId: string, calendarId: string): CalendarResource | undefined => {
    const integration = integrations.find(i => i.id === integrationId)
    return integration?.calendars.find(c => c.id === calendarId)
  }, [integrations])

  // Fetch calendars on mount and when user changes
  useEffect(() => {
    fetchCalendars()
  }, [fetchCalendars])

  const value = useMemo(() => ({
    integrations,
    loading,
    refreshCalendars: fetchCalendars,
    refreshCalendarsForIntegration,
    reconnect,
    disconnect,
    findCalendar,
  }), [integrations, loading, fetchCalendars, refreshCalendarsForIntegration, reconnect, disconnect, findCalendar])

  return (
    <CalendarsContext.Provider value={value}>
      {children}
    </CalendarsContext.Provider>
  )
}

export function useCalendars() {
  const context = useContext(CalendarsContext)
  if (context === undefined) {
    throw new Error('useCalendars must be used within a CalendarsProvider')
  }
  return context
}
